import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { useStore } from '../../store';
import { UserRole, SubscriptionType } from '@spellion/shared';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * Layout Component
 * 
 * Main layout wrapper for all pages in the Spellion application.
 * Provides consistent header, footer, and content area with smooth page transitions.
 * 
 * Uses mobile-first responsive design:
 * - Mobile: < 768px (default)
 * - Tablet: 768px - 1023px (md breakpoint)
 * - Desktop: >= 1024px (lg breakpoint)
 * 
 * Page transitions:
 * - Smooth fade-in-up animation (300ms with ease-out) on route changes
 * - Respects user's prefers-reduced-motion setting via CSS
 * 
 * Accessibility features:
 * - Skip to main content link for keyboard navigation
 * - Proper landmark regions (header, main, footer)
 * - WCAG AA compliant color contrast (4.5:1 minimum)
 * 
 * Dark mode:
 * - Loads user theme preference from server when authenticated
 * - Falls back to localStorage for non-authenticated users
 * 
 * @validates Requirements 10.1, 10.2, 11.1 (page transitions 200-400ms), 11.5
 */
export function Layout() {
  // Use location key to trigger re-animation on route change
  const location = useLocation();
  const { t } = useTranslation();
  
  // Get state from store using single selector to avoid hooks issues
  const user = useStore((state) => state.user);
  const token = useStore((state) => state.token);
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const logout = useStore((state) => state.logout);
  const resetTheme = useStore((state) => state.resetTheme);
  const activeDictionary = useStore((state) => state.activeDictionary);
  const loadDefaultDictionary = useStore((state) => state.loadDefaultDictionary);
  const setActiveDictionary = useStore((state) => state.setActiveDictionary);
  const setAvailableDictionaries = useStore((state) => state.setAvailableDictionaries);
  const loadThemeFromServer = useStore((state) => state.loadThemeFromServer);
  
  // Compute isAdmin and isPremium directly from user
  const isAdmin = user?.role === UserRole.ADMIN;
  const isPremium = user?.subscriptionType === SubscriptionType.PREMIUM || isAdmin;

  // Handle logout with theme reset
  const handleLogout = () => {
    resetTheme(); // Reset to light mode
    logout();
  };

  // Load user theme preference from server when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      loadThemeFromServer(token);
    }
  }, [isAuthenticated, token, loadThemeFromServer]);

  // Load dictionaries and set the active one based on user type
  useEffect(() => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
    
    const initializeDictionary = async () => {
      // First, always load the admin-configured default dictionary
      await loadDefaultDictionary();
      
      // For Premium/Admin users: load available dictionaries and their saved preference
      if (isPremium && token && isAuthenticated && user?.id) {
        try {
          // Load available dictionaries
          const response = await fetch(`${API_BASE_URL}/dictionaries`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          if (data.success && data.data) {
            const activeDicts = data.data.filter((d: { isActive: boolean }) => d.isActive);
            setAvailableDictionaries(activeDicts);
            
            // Load user's saved dictionary preference from server
            const prefResponse = await fetch(`${API_BASE_URL}/dictionaries/preference`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const prefData = await prefResponse.json();
            
            if (prefData.success && prefData.data) {
              // User has a saved preference - use it
              const preferredDict = activeDicts.find((d: { id: string }) => d.id === prefData.data.id);
              if (preferredDict) {
                setActiveDictionary(preferredDict);
              }
              // If preferred dictionary not found in active list, keep the default
            }
            // If no preference saved (prefData.data is null), keep the admin default
          }
        } catch (error) {
          console.error('Failed to load dictionaries:', error);
        }
      }
      // For Free users: the default dictionary loaded above is already set, nothing more to do
    };
    
    initializeDictionary();
  }, [isPremium, token, isAuthenticated, user?.id, loadDefaultDictionary, setAvailableDictionaries, setActiveDictionary]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
      {/* Skip to main content link for keyboard users - hidden until focused */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:font-medium focus:shadow-lg"
      >
        {t('a11y.skipToContent')}
      </a>
      
      {/* Header with navigation - connected to auth store */}
      <Header 
        activeDictionary={activeDictionary?.name ?? 'ODS'}
        user={user ? {
          email: user.email,
          role: user.role,
          subscriptionType: user.subscriptionType,
        } : null}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        onLogout={handleLogout}
      />
      
      {/* Main content area with page transition animation */}
      <main 
        key={location.pathname}
        id="main-content"
        className="flex-1 container-app py-6 md:py-8 lg:py-10 animate-page-enter"
        tabIndex={-1}
      >
        <Outlet />
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
}
